import { Zone, Zones } from '../Zone';
import { TimelineSlot, Widget } from '../Widget';
import { ITimeline } from './types';

/**
 * Interleaves all content within a specific loop
 */
export function interleave(arr: TimelineSlot[], arr2: TimelineSlot[]): TimelineSlot[] {
    //Maintain the groups, ensure they do not get shuffled
    const left = consolidateGroups(arr.length > arr2.length ? arr : arr2);
    const right = consolidateGroups(arr.length > arr2.length ? arr2 : arr);

    const result = [];

    let leftIndex = 0;

    //nested for loop, for every slot in 'currentSlots'
    for (let i = 0; i < right.length; ++i) {
        for (let j = 0; j < Math.round(left.length / (right.length + 1)); ++j) {
            if (leftIndex < left.length) {
                result.push(left[leftIndex]);
                leftIndex++;
            }
        }

        result.push(right[i]);
    }

    for (let i = leftIndex; i < left.length; ++i) {
        result.push(left[i]);
    }

    //Flatten any grouped slots
    return result.flat();
}

/**
 * Group slots with the same groupIdentifier into a single index
 * Used for the interleave function so that grouped items are not shuffled
 * Example: [G1, G2, G2, G2, G3, G3, G4] => [G1, [G2, G2, G2], [G3, G3], G4]
 */
function consolidateGroups(slots: TimelineSlot[]): Array<TimelineSlot | TimelineSlot[]> {
    const groupManifest: { [key: string]: TimelineSlot[] } = {};
    const groupedSlots: Array<TimelineSlot | TimelineSlot[]> = [];

    slots.forEach((slot) => {
        if (slot.groupIdentifier) {
            if (Object.prototype.hasOwnProperty.call(groupManifest, slot.groupIdentifier)) {
                groupManifest[slot.groupIdentifier].push(slot);
            } else {
                groupManifest[slot.groupIdentifier] = [slot];
                groupedSlots.push(groupManifest[slot.groupIdentifier]);
            }
        } else {
            groupedSlots.push(slot);
        }
    });

    return groupedSlots;
}

export abstract class TimelineBase implements ITimeline {
    /**
     * Index of current item in loop
     */
    private currentLoopItemIndex: number;

    /**
     * list of widget slots within current loop
     */
    private currentLoop: TimelineSlot[];

    /**
     * widget slots within next loop
     */
    private nextLoop: TimelineSlot[];

    /**
     * The active widget's current slot
     */
    private currentSlot: TimelineSlot | null;

    /**
     * Array - List of widgets used in timeline
     * @type {Array.<Widget>}
     */
    protected timelineWidgets: Widget[];

    constructor() {
        this.currentLoopItemIndex = -1;
        this.timelineWidgets = [];

        this.currentLoop = this.buildTimelineLoop();
        this.nextLoop = this.buildTimelineLoop();
        this.currentSlot = this.currentLoop.length > 0 ? this.currentLoop[0] : null;
    }

    public getCurrentSlot(): TimelineSlot | null {
        return this.currentSlot;
    }

    public abortLoop(): void {
        this.currentLoopItemIndex = -1;
        this.currentLoop = [];
        this.currentSlot = this.nextLoop.length > 0 ? this.nextLoop[0] : null;
    }

    /**
     * creates new loop object instance
     */
    protected abstract buildTimelineLoop(): TimelineSlot[];

    public nextSlot(suppressTriggers: boolean, zone: Zone): TimelineSlot | null {
        ++this.currentLoopItemIndex;

        if (this.currentLoopItemIndex >= this.currentLoop.length) {
            if (!suppressTriggers) {
                const interrupts = zone.getEventInterruptions('onLoopComplete');
                //we must wait until all promises have resolved before we can
                //call onLoopComplete event or generate a new loop... this is
                //gonna be tough...
                interrupts.forEach((interrupt) => {
                    interrupt.resolve();
                });

                const onLoopComplete = zone.getTrigger('onLoopComplete');
                if (onLoopComplete) {
                    Zones.setCurrentZoneId(zone.id);
                    onLoopComplete();
                }
            }

            // sets current loop to next generated loop
            this.currentLoop = this.nextLoop;
            this.nextLoop = this.buildTimelineLoop();
            this.currentLoopItemIndex = 0;
            this.currentSlot = this.currentLoop.length > 0 ? this.currentLoop[0] : null;
        } else {
            this.currentSlot = this.currentLoop[this.currentLoopItemIndex];
        }

        return this.currentSlot;
    }

    public peakNext(): TimelineSlot | null {
        if (this.currentLoopItemIndex + 1 >= this.currentLoop.length) {
            return this.nextLoop.length > 0 ? this.nextLoop[0] : null;
        } else {
            return this.currentLoop[this.currentLoopItemIndex + 1];
        }
    }

    public sortWidgets(widgetArray: Widget[]): void {
        widgetArray.sort((widgetA, widgetB) => {
            return widgetA.order - widgetB.order;
        });

        this.timelineWidgets = widgetArray;
        this.nextLoop = this.buildTimelineLoop();
    }

    /**
     * Remove non-override widgets if the overrideWidget has content
     */
    protected filterWidgets(widgets: Widget[], overrideWidget?: Widget): Widget[] {
        if (overrideWidget && widgets.length > 0) {
            if (overrideWidget.hasContentToPlay) {
                //override has content - remove all widgets that aren't override
                for (let x = 0; x < widgets.length; x++) {
                    if (!widgets[x].override) {
                        widgets.splice(x, 1);
                        x--;
                    }
                }
            } else {
                //remove the override widget from list..
                for (let x = 0; x < widgets.length; x++) {
                    if (widgets[x].id === overrideWidget.id) {
                        widgets.splice(x, 1);
                        x--;
                    }
                }
            }
        }

        return widgets;
    }

    /**
     * Gets the first widget that has 'override' set.
     */
    protected getOverrideWidget(widgets: Widget[]): Widget | undefined {
        let overrideWidget: Widget | undefined;

        if (widgets.length > 0) {
            widgets.forEach((widget) => {
                //see if an override exists in one of the widgets
                if (!overrideWidget && widget.override) {
                    overrideWidget = widget;
                }
            });
        }

        return overrideWidget;
    }
}
